import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { SaveRounded, ClearRounded } from "@mui/icons-material";
import { READ_COMPANY_BANK, UPDATE_COMPANY_BANK, } from '../../../services/apiRoutes';
import CustomButton from '../../common/CustomButton';
import CustomTextField from '../../common/CustomTextField';
import CustomStyle from '../../common/CustomStyle';
import CustomAutocomplete from '../../common/CustomAutoComplete';
import CustomTextarea from '../../common/CustomTextarea';
import { getCountries } from '../../../services/CommonServices';
import { t } from '../../../constants/translationHelper';

const ValidationSchema = Yup.object().shape({
    defaultcompanyId: Yup.string().required("company is required"),
    bankName: Yup.string().required("Bank Name is required"),
    accountNumber: Yup.string().required("Account Number is required"),
    accountHolderName: Yup.string().required("Account Holder Name is required"),
    swiftCode: Yup.string().required("SWIFT Code is required"),
    ifscCode: Yup.string().required("IFSC Code is required"),
    bankBranch: Yup.string().required("Bank Branch is required"),
    countryId: Yup.string().required("Country is required"),
    mobile: Yup.string().required("Mobile number is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
});

const UpdateCompanyBank = ({ handleClose, setMessage, companyBankId, fetchData, getAllCompany }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [conutries, setConutries] = useState([]);

    const getCommonData = async () => {
        const getConutriesData = await getCountries()
        setConutries(getConutriesData)
    }
    useEffect(() => {
        getCommonData()
    }, [])


    useEffect(() => {
        if (!companyBankId) return;

        const fetchData = async () => {
            try {
                const accessToken = sessionStorage.getItem('accessToken');
                const response = await axios.get(READ_COMPANY_BANK(companyBankId), {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                setData(response.data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [companyBankId]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                defaultcompanyId: values.defaultcompanyId,
                bankName: values.bankName,
                accountNumber: values.accountNumber,
                accountHolderName: values.accountHolderName,
                swiftCode: values.swiftCode,
                ifscCode: values.ifscCode,
                bankBranch: values.bankBranch,
                bankAddress: values.bankAddress,
                countryId: values.countryId,
                mobile: values.mobile,
                email: values.email,
                ibanNumber: values.ibanNumber,

            };

            const accessToken = sessionStorage.getItem('accessToken');
            const response = await axios.put(UPDATE_COMPANY_BANK(companyBankId), payload, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (response.status === 200) {
                setMessage({
                    open: true,
                    text: response.data.message || "User Updated successfully",
                    type: "success",
                });
                setTimeout(() => {
                    fetchData();
                    handleClose();
                }, 500);
            } else {
                setMessage({
                    open: true,
                    text: "Something went wrong",
                    type: "error",
                });
            }
        } catch (error) {
            setMessage({
                open: true,
                text: "Something went wrong",
                type: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={{
                    defaultcompanyId: parseInt(data.defaultcompanyId || ''),
                    bankName: data.bankName || '',
                    accountNumber: data.accountNumber || '',
                    accountHolderName: data.accountHolderName || '',
                    swiftCode: data.swiftCode || '',
                    ifscCode: data.ifscCode || '',
                    bankBranch: data.bankBranch || '',
                    bankAddress: data.bankAddress || '',
                    countryId: parseInt(data.countryId || ''),
                    mobile: data.mobile || '',
                    email: data.email || '',
                    ibanNumber: data.ibanNumber || '',
                }}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, handleChange, values, handleBlur, setFieldValue }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Update Company Bank")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={1} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} sm={6}>
                                <CustomAutocomplete
                                    required={true}
                                    options={getAllCompany}
                                    fieldLabel="Select Company"
                                    getOptionLabel={(option) => option.title}
                                    value={getAllCompany.find((option) => option.id === values.defaultcompanyId) || null}
                                    onChange={(e, newValue) => {
                                        setFieldValue('defaultcompanyId', newValue ? newValue.id : '');
                                    }}
                                    placeholder="Select Company"
                                    error={touched.defaultcompanyId && !!errors.defaultcompanyId}
                                    helperText={touched.defaultcompanyId && errors.defaultcompanyId}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="bankName"
                                    fieldLabel="Bank Name"
                                    value={values.bankName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.bankName && Boolean(errors.bankName)}
                                    helperText={touched.bankName && errors.bankName}
                                    required
                                    placeholder='Enter Bank Name'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="accountNumber"
                                    fieldLabel="Account Number"
                                    value={values.accountNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.accountNumber && Boolean(errors.accountNumber)}
                                    helperText={touched.accountNumber && errors.accountNumber}
                                    required
                                    placeholder='Enter Account Number'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="accountHolderName"
                                    fieldLabel="Account Holder Name"
                                    value={values.accountHolderName}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.accountHolderName && Boolean(errors.accountHolderName)}
                                    helperText={touched.accountHolderName && errors.accountHolderName}
                                    required
                                    placeholder='Enter Account Holder Name'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="swiftCode"
                                    fieldLabel="SWIFT Code"
                                    value={values.swiftCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.swiftCode && Boolean(errors.swiftCode)}
                                    helperText={touched.swiftCode && errors.swiftCode}
                                    required
                                    placeholder='Enter SWIFT Code'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="ifscCode"
                                    fieldLabel="IFSC Code"
                                    value={values.ifscCode}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.ifscCode && Boolean(errors.ifscCode)}
                                    helperText={touched.ifscCode && errors.ifscCode}
                                    required
                                    placeholder='Enter IFSC Code'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="ibanNumber"
                                    fieldLabel="IBAN Number"
                                    value={values.ibanNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder='Enter IBAN Number'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="bankBranch"
                                    fieldLabel="Bank Branch"
                                    value={values.bankBranch}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.bankBranch && Boolean(errors.bankBranch)}
                                    helperText={touched.bankBranch && errors.bankBranch}
                                    required
                                    placeholder='Enter Bank Branch'
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <CustomAutocomplete
                                    required={true}
                                    options={conutries}
                                    fieldLabel="Country"
                                    getOptionLabel={(option) => option.title}
                                    value={conutries.find((option) => option.id === values.countryId) || null}
                                    onChange={(e, newValue) => {
                                        setFieldValue('countryId', newValue ? newValue.id : '');
                                    }}
                                    placeholder="Select Country"
                                    error={touched.countryId && !!errors.countryId}
                                    helperText={touched.countryId && errors.countryId}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="mobile"
                                    type="number"
                                    fieldLabel="Mobile"
                                    value={values.mobile}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.mobile && Boolean(errors.mobile)}
                                    helperText={touched.mobile && errors.mobile}
                                    required
                                    placeholder='Enter Mobile'
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <CustomTextField
                                    name="email"
                                    type='email'
                                    fieldLabel="Email"
                                    value={values.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={touched.email && Boolean(errors.email)}
                                    helperText={touched.email && errors.email}
                                    required
                                    placeholder='Enter Email'
                                />
                            </Grid>
                            <Grid item xs={8}>
                                <CustomTextarea
                                    name="bankAddress"
                                    fieldLabel="Bank Address"
                                    value={values.bankAddress}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder='Enter Bank Address'
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default UpdateCompanyBank;
